import React from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import dayjs from 'dayjs';

import { ChoiceFormField, FormField, MergeFieldRegistryEntry, RichTextFormField } from '~forms/types';
import { optionValue, splitChoiceValue } from '~forms/lib/choiceValue';
import { InlineTokenText } from './MergeFieldToken';
import { RichTextView } from './rich-text';

interface ComponentProps {
  isOpen: boolean;
  toggle: () => void;
  title: string;
  fields: FormField[];
  registry: MergeFieldRegistryEntry[];
}

// Resolve a date default to a human date for the preview ('today'/'yesterday' keywords or an ISO date).
function formatDateDefault(dv: string): string {
  if (!dv) return '';
  if (dv === 'today') return dayjs().format('DD/MM/YYYY');
  if (dv === 'yesterday') return dayjs().subtract(1, 'day').format('DD/MM/YYYY');
  const parsed = dayjs(dv);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : dv;
}

/**
 * Read-only preview of a form template as a person filling it out would see it.
 * Merge tokens render as labelled chips (no live resolution — that's the fill experience, TAV-2661).
 */
const FormPreview: React.FC<ComponentProps> = ({ isOpen, toggle, title, fields, registry }) => {
  const renderControl = (field: FormField) => {
    const dv = field.default_value ?? '';
    const emptyDash = <span className="text-muted">—</span>;
    // Editable fields look like a fillable input box; read-only fields render as plain static text.
    const boxClass = field.read_only ? 'form-preview-static' : 'form-preview-value';

    switch (field.type) {
      case 'single_line_text':
        return <div className={boxClass}>{dv ? <InlineTokenText text={dv} registry={registry} /> : emptyDash}</div>;
      case 'paragraph_text':
        return (
          <div className={`${boxClass} ${boxClass}--multiline`}>
            {dv ? <InlineTokenText text={dv} registry={registry} /> : emptyDash}
          </div>
        );
      case 'date':
        return <div className={boxClass}>{formatDateDefault(dv) || emptyDash}</div>;
      case 'checkbox':
        // Matches the fill page: the help text is the checkbox's caption, inline to the right of the box
        // (the shared help block above is skipped for checkboxes, see below).
        return (
          <div className="form-preview-checkbox">
            <input type="checkbox" checked={dv === 'true'} disabled readOnly />
            {field.help_text && (
              <span className="form-fill-checkbox-help">
                <InlineTokenText text={field.help_text} registry={registry} />
              </span>
            )}
          </div>
        );
      case 'multiple_choice':
      case 'dropdown': {
        const choiceField = field as Extract<FormField, { type: 'multiple_choice' | 'dropdown' }>;
        const opts = (choiceField as ChoiceFormField).options ?? [];
        if (field.type === 'dropdown') {
          const selected = opts.find((o) => (o.value || o.label) === dv);
          return (
            <div className="form-preview-value">{selected?.label ?? <span className="text-muted">— Select —</span>}</div>
          );
        }
        // Multi-select shows checkboxes (any number of defaults ticked); single-select shows radios.
        const allowMultiple = !!(choiceField as ChoiceFormField).allow_multiple;
        const defaults = allowMultiple ? splitChoiceValue(dv) : [dv];
        return (
          <div className="form-preview-choice">
            {opts.length === 0 && <span className="text-muted">No options</span>}
            {opts.map((opt) => (
              <div key={opt.id} className="form-preview-choice-item">
                <input
                  type={allowMultiple ? 'checkbox' : 'radio'}
                  disabled
                  readOnly
                  checked={defaults.includes(optionValue(opt))}
                />
                <span>{opt.label}</span>
              </div>
            ))}
          </div>
        );
      }
      case 'signature':
        return <div className="form-preview-signature">Signature</div>;
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable data-cy="form-preview-modal">
      <ModalHeader toggle={toggle}>Preview - {title}</ModalHeader>
      <ModalBody>
        <div className="form-preview">
          {fields.length === 0 && <p className="text-muted">This form has no fields yet.</p>}
          {fields.map((field) => {
            if (field.type === 'section_divider') {
              return (
                <h5 key={field.id} className="form-preview-section">
                  {field.label || 'Section'}
                </h5>
              );
            }
            if (field.type === 'rich_text') {
              // Filler sees the same formatting, view-only — no label, input, or required marker (e.g. T&Cs).
              return (
                <div key={field.id} className="form-preview-rich-text">
                  <RichTextView html={(field as RichTextFormField).content} registry={registry} />
                </div>
              );
            }
            const isRequired = field.required && !field.read_only;
            return (
              <div key={field.id} className="form-preview-field">
                <label className="form-preview-label">
                  {field.label ? (
                    <InlineTokenText text={field.label} registry={registry} />
                  ) : (
                    <span className="text-muted">Untitled field</span>
                  )}
                  {isRequired && <span className="text-danger ms-1" aria-hidden="true">*</span>}
                  {field.read_only && <span className="form-preview-readonly-tag">read only</span>}
                </label>
                {field.help_text && field.type !== 'checkbox' && (
                  <div className="form-preview-help">
                    <InlineTokenText text={field.help_text} registry={registry} />
                  </div>
                )}
                {renderControl(field)}
              </div>
            );
          })}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default FormPreview;
